/**
 * SpriteRenderer — creates a PixiJS Sprite from a blob URL texture.
 *
 * Receives pre-resolved blob URLs (from useAssetUrl / File System Access API)
 * and loads them through the shared PixiTextureCache.
 */
import { Container, Sprite, Texture } from 'pixi.js';
import { pixiTextureCache } from '@renderer/pixi/PixiTextureCache';
import type { GameObject, SpriteRendererData } from '@core/types/gameobject.types';

export class SpriteRenderer {
  /**
   * Create a PixiJS container with a Sprite child.
   * Async because texture loading may require a network fetch on first access.
   */
  static async create(gameObject: GameObject, blobUrl: string | null): Promise<Container> {
    const container = new Container();
    const spriteData = gameObject.components.find(c => c.type === 'sprite-renderer')
      ?.data as SpriteRendererData | undefined;

    if (blobUrl) {
      const texture = await pixiTextureCache.getTexture(blobUrl);
      const sprite = new Sprite(texture);
      sprite.anchor.set(gameObject.transform.anchor.x, gameObject.transform.anchor.y);

      if (spriteData) {
        // Tint: convert hex string (#RRGGBB) to number
        if (spriteData.tint && spriteData.tint !== '#ffffff') {
          sprite.tint = parseInt(spriteData.tint.replace('#', ''), 16);
        }
        sprite.alpha = spriteData.alpha ?? 1;
        sprite.scale.x = spriteData.flipX ? -1 : 1;
        sprite.scale.y = spriteData.flipY ? -1 : 1;
      }

      container.addChild(sprite);
    }

    return container;
  }

  /**
   * Update an existing sprite container with new game object state.
   * Re-applies tint, alpha, flip, and anchor. Swaps texture if the blob URL changed.
   */
  static async update(
    container: Container,
    gameObject: GameObject,
    blobUrl: string | null
  ): Promise<void> {
    const spriteData = gameObject.components.find(c => c.type === 'sprite-renderer')
      ?.data as SpriteRendererData | undefined;

    // Find existing sprite child (if any)
    const existingSprite = container.children.find(c => c instanceof Sprite) as Sprite | undefined;

    if (!blobUrl) {
      // No texture — remove sprite if it exists
      if (existingSprite) {
        container.removeChild(existingSprite);
        existingSprite.destroy();
      }
      return;
    }

    if (existingSprite) {
      // Check if texture changed by comparing source
      const currentSrc = existingSprite.texture?.source?.label ?? '';
      if (currentSrc !== blobUrl) {
        const texture = await pixiTextureCache.getTexture(blobUrl);
        existingSprite.texture = texture;
      }

      // Update properties
      existingSprite.anchor.set(
        gameObject.transform.anchor.x,
        gameObject.transform.anchor.y
      );
      if (spriteData) {
        if (spriteData.tint && spriteData.tint !== '#ffffff') {
          existingSprite.tint = parseInt(spriteData.tint.replace('#', ''), 16);
        } else {
          existingSprite.tint = 0xffffff;
        }
        existingSprite.alpha = spriteData.alpha ?? 1;
        existingSprite.scale.x = spriteData.flipX ? -1 : 1;
        existingSprite.scale.y = spriteData.flipY ? -1 : 1;
      }
    } else {
      // No sprite yet — create one
      const texture = await pixiTextureCache.getTexture(blobUrl);
      const sprite = new Sprite(texture);
      sprite.anchor.set(gameObject.transform.anchor.x, gameObject.transform.anchor.y);

      if (spriteData) {
        if (spriteData.tint && spriteData.tint !== '#ffffff') {
          sprite.tint = parseInt(spriteData.tint.replace('#', ''), 16);
        }
        sprite.alpha = spriteData.alpha ?? 1;
        sprite.scale.x = spriteData.flipX ? -1 : 1;
        sprite.scale.y = spriteData.flipY ? -1 : 1;
      }

      container.addChild(sprite);
    }
  }
}
